import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label?: string;
  /** Optional group heading (e.g. tool module). Ungrouped when omitted. */
  group?: string;
}

interface MultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: MultiSelectOption[];
  /** Allow adding a typed value that isn't in `options` (e.g. a new KB tag). */
  creatable?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * A searchable multi-select backed by a predefined option list. Selected values
 * show as removable chips in the trigger; the popover lists options (optionally
 * grouped) with a check on the selected ones. With `creatable`, a typed value
 * not in the list can be added — used for open-ended fields like KB tags, while
 * strict fields (e.g. the tool allowlist) leave it off.
 *
 * For a compact toolbar pill (numeric ids, avatars, a Clear row), use `MultiPopover`.
 */
export function MultiSelect({
  value,
  onChange,
  options,
  creatable = false,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  disabled,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const labelFor = useMemo(() => {
    const map = new Map(options.map((o) => [o.value, o.label ?? o.value]));
    return (v: string) => map.get(v) ?? v;
  }, [options]);

  const add = (v: string) => {
    if (!value.includes(v)) onChange([...value, v]);
  };
  const remove = (v: string) => onChange(value.filter((x) => x !== v));
  const toggle = (v: string) => (value.includes(v) ? remove(v) : add(v));

  const trimmed = query.trim();
  const q = trimmed.toLowerCase();

  // Filter + group (preserving option order within each group). Plain compute —
  // the React Compiler memoizes; a manual useMemo on the derived `q` isn't kept.
  const byGroup = new Map<string, MultiSelectOption[]>();
  for (const o of options) {
    if (q && !(o.label ?? o.value).toLowerCase().includes(q) && !o.value.toLowerCase().includes(q)) {
      continue;
    }
    const key = o.group ?? '';
    (byGroup.get(key) ?? byGroup.set(key, []).get(key)!).push(o);
  }
  const groups = [...byGroup.entries()];

  const exists = options.some(
    (o) => o.value.toLowerCase() === q || (o.label ?? '').toLowerCase() === q,
  );
  const canCreate = creatable && trimmed.length > 0 && !exists && !value.includes(trimmed);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'border-input focus-visible:ring-ring flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border bg-transparent px-2 py-1 text-sm focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {value.length === 0 ? (
            <span className="text-muted-foreground px-1">{placeholder}</span>
          ) : (
            value.map((v) => (
              <Badge key={v} variant="secondary" className="gap-1 font-normal">
                {labelFor(v)}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${labelFor(v)}`}
                  className="hover:bg-background/60 rounded"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    remove(v);
                  }}
                >
                  <X className="size-3 opacity-70" />
                </span>
              </Badge>
            ))
          )}
          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            className="rounded-md"
          />
          <CommandList>
            {groups.length === 0 && !canCreate ? (
              <div className="text-muted-foreground p-3 text-sm">{emptyText}</div>
            ) : null}
            {groups.map(([name, opts]) => (
              <CommandGroup key={name || '_'} heading={name || undefined}>
                {opts.map((o) => {
                  const selected = value.includes(o.value);
                  return (
                    <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)}>
                      <Check className={cn('mr-2 size-4', selected ? 'opacity-100' : 'opacity-0')} />
                      {o.label ?? o.value}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
            {canCreate ? (
              <CommandGroup>
                <CommandItem
                  value={`__create__${trimmed}`}
                  onSelect={() => {
                    add(trimmed);
                    setQuery('');
                  }}
                >
                  <Plus className="mr-2 size-4" /> Create “{trimmed}”
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
